import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import adminConsoleService, {
  type WizardLayer,
  type SecurityGroup,
} from '@services/adminConsoleService';
import { LayerRow } from '../layers/LayerRow';

const DEFAULT_COLOR = '#ffc989';

const generateTempId = () => -Math.floor(Math.random() * 1_000_000 + 1);

// Derived type: subgroup entry scoped to a layer
interface LayerSecurityGroup {
  SecurityGroupID: number;
  Name: string;
  GroupName: string;
}

export const LayersTab: React.FC = () =>
{
  const [layers, setLayers] = useState<WizardLayer[]>([]);
  const [originalLayers, setOriginalLayers] = useState<WizardLayer[]>([]);
  const [changes, setChanges] = useState<Set<number>>(new Set());
  const [newLayerIds, setNewLayerIds] = useState<Set<number>>(new Set());
  const [justAddedId, setJustAddedId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  // Security groups data for the layer rows
  const [allSecurityGroups, setAllSecurityGroups] = useState<SecurityGroup[]>([]);
  // layerId → assigned subgroups
  const [layerSecGroupMap, setLayerSecGroupMap] = useState<Map<number, LayerSecurityGroup[]>>(
    new Map(),
  );

  // Edit security groups modal
  const [editingLayerId, setEditingLayerId] = useState<number | null>(null);
  const [sgSearch, setSgSearch] = useState('');
  const [selectedSgIds, setSelectedSgIds] = useState<Set<number>>(new Set());
  const [expandedSgGroups, setExpandedSgGroups] = useState<Set<number>>(new Set());
  const [savingSg, setSavingSg] = useState(false);

  useEffect(() =>
  {
    setLoading(true);
    Promise.all([adminConsoleService.getLayers(), adminConsoleService.getSecurityGroups()])
      .then(([layerData, sgData]) =>
      {
        const filtered = layerData
          .filter(l => l.LayerId >= 1)
          .sort((a, b) => a.LayerId - b.LayerId);
        setLayers(filtered);
        setOriginalLayers(filtered);
        setAllSecurityGroups(sgData.filter(g => !g.IsDeleted));

        // Build map: layerId → assigned subgroups
        const map = new Map<number, LayerSecurityGroup[]>();
        for (const group of sgData)
        {
          for (const sg of group.SecurityGroups)
          {
            if (sg.IsDeleted) continue;
            for (const sl of sg.SecurityGroupLayers)
            {
              if (!map.has(sl.LayerId)) map.set(sl.LayerId, []);
              map.get(sl.LayerId)!.push({
                SecurityGroupID: sg.SecurityGroupID,
                Name: sg.Name,
                GroupName: group.Name,
              });
            }
          }
        }
        setLayerSecGroupMap(map);
      })
      .catch(() => setError('Failed to load layers. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = useCallback(
    (
      layerId: number,
      field: 'Name' | 'Color' | 'TextColor' | 'IsEnable',
      value: string | boolean,
    ) =>
    {
      console.log('Change:', layerId, field, value);
      setLayers(prev => prev.map(l => (l.LayerId === layerId ? { ...l, [field]: value } : l)));
      setChanges(prev => new Set(prev).add(layerId));
      setSaveSuccess(false);
    },
    [],
  );

  const handleAddLayer = () =>
  {
    const tempId = generateTempId();
    const newLayer: WizardLayer = {
      LayerId: tempId,
      Name: 'New Layer',
      Color: DEFAULT_COLOR,
      TextColor: false,
      IsEnable: true,
    };
    setSearch(''); // clear search so the new row is visible
    setLayers(prev => [...prev, newLayer]);
    setChanges(prev => new Set(prev).add(tempId));
    setNewLayerIds(prev => new Set(prev).add(tempId));
    setJustAddedId(tempId);
  };

  const handleCancel = () =>
  {
    setLayers(originalLayers);
    setChanges(new Set());
    setNewLayerIds(new Set());
    setJustAddedId(null);
    setError(null);
  };

  // Scroll to bottom and auto-focus the new layer name when justAddedId changes
  useEffect(() =>
  {
    if (justAddedId === null) return;
    const frame = requestAnimationFrame(() =>
    {
      if (listRef.current)
      {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [justAddedId]);

  const handleSave = async () =>
  {
    const modified = layers.filter(l => changes.has(l.LayerId));
    if (modified.length === 0) return;

    setSaving(true);
    setError(null);
    try
    {
      await adminConsoleService.updateLayers(
        modified.map(l => ({
          LayerId: l.LayerId,
          Name: l.Name,
          Color: l.Color,
          TextColor: l.TextColor,
          IsEnable: l.IsEnable,
          DeleteSecurityGroups: false,
        })),
      );
      setOriginalLayers(layers);
      setChanges(new Set());
      setNewLayerIds(new Set());
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      window.dispatchEvent(new CustomEvent('C2_layers_changed'));
    }
    catch
    {
      setError('Failed to save changes. Please try again.');
    }
    finally
    {
      setSaving(false);
    }
  };

  // ── Edit security groups for a layer ─────────────────────────────────────
  const openEditSg = (layerId: number) =>
  {
    const assigned = layerSecGroupMap.get(layerId) ?? [];
    setSelectedSgIds(new Set(assigned.map(sg => sg.SecurityGroupID)));
    // Auto-expand groups that have selected children
    const groupIds = new Set(
      allSecurityGroups
        .filter(g =>
          g.SecurityGroups.some(sg => assigned.some(a => a.SecurityGroupID === sg.SecurityGroupID)),
        )
        .map(g => g.GroupId),
    );
    setExpandedSgGroups(groupIds);
    setSgSearch('');
    setEditingLayerId(layerId);
  };

  const toggleSgGroupExpand = (groupId: number) =>
  {
    setExpandedSgGroups(prev =>
    {
      const next = new Set(prev);
      next.has(groupId) ? next.delete(groupId) : next.add(groupId);
      return next;
    });
  };

  const getSgGroupState = (group: SecurityGroup): 'all' | 'some' | 'none' =>
  {
    const children = group.SecurityGroups.filter(sg => !sg.IsDeleted);
    const sel = children.filter(sg => selectedSgIds.has(sg.SecurityGroupID));
    if (sel.length === 0) return 'none';
    if (sel.length === children.length) return 'all';
    return 'some';
  };

  const toggleSgGroupAll = (group: SecurityGroup) =>
  {
    const children = group.SecurityGroups.filter(sg => !sg.IsDeleted);
    const state = getSgGroupState(group);
    setSelectedSgIds(prev =>
    {
      const next = new Set(prev);
      if (state === 'all') children.forEach(sg => next.delete(sg.SecurityGroupID));
      else children.forEach(sg => next.add(sg.SecurityGroupID));
      return next;
    });
  };

  const toggleSgSubGroup = (id: number) =>
  {
    setSelectedSgIds(prev =>
    {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const saveLayerSecurityGroups = async () =>
  {
    if (editingLayerId === null) return;
    setSavingSg(true);

    // Build assigned list for optimistic update + payload
    const assigned: LayerSecurityGroup[] = [];
    const sgPayload: { SecurityGroupID: number; Name: string }[] = [];
    for (const group of allSecurityGroups)
    {
      for (const sg of group.SecurityGroups)
      {
        if (!sg.IsDeleted && selectedSgIds.has(sg.SecurityGroupID))
        {
          assigned.push({
            SecurityGroupID: sg.SecurityGroupID,
            Name: sg.Name,
            GroupName: group.Name,
          });
          sgPayload.push({ SecurityGroupID: sg.SecurityGroupID, Name: sg.Name });
        }
      }
    }

    // Optimistic update
    setLayerSecGroupMap(prev =>
    {
      const next = new Map(prev);
      next.set(editingLayerId, assigned);
      return next;
    });
    setEditingLayerId(null);

    try
    {
      const layer = layers.find(l => l.LayerId === editingLayerId);
      if (!layer) return;
      await adminConsoleService.updateLayers([
        {
          LayerId: layer.LayerId,
          Name: layer.Name,
          Color: layer.Color,
          TextColor: layer.TextColor,
          IsEnable: layer.IsEnable,
          DeleteSecurityGroups: false,
          SecurityGroups: sgPayload,
        },
      ]);
    }
    catch
    {
      // Rollback
      setLayerSecGroupMap(prev =>
      {
        const next = new Map(prev);
        next.set(editingLayerId, layerSecGroupMap.get(editingLayerId) ?? []);
        return next;
      });
      setError('Failed to save security groups.');
    }
    finally
    {
      setSavingSg(false);
    }
  };

  const filteredSgGroups = allSecurityGroups
    .map(g => ({
      ...g,
      SecurityGroups: g.SecurityGroups.filter(
        sg =>
          !sg.IsDeleted &&
          (!sgSearch.trim() ||
            sg.Name.toLowerCase().includes(sgSearch.toLowerCase()) ||
            g.Name.toLowerCase().includes(sgSearch.toLowerCase())),
      ),
    }))
    .filter(
      g => g.SecurityGroups.length > 0 || g.Name.toLowerCase().includes(sgSearch.toLowerCase()),
    );

  const editingLayer = layers.find(l => l.LayerId === editingLayerId);

  const filtered = layers.filter(l => l.Name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="flex flex-col h-full">
      {/* Description */}
      <div className="px-6 pt-5 pb-4">
        <p className="text-[#62aacb] text-[16px] font-medium leading-snug">
          Corgee users can have access to different information layers.
        </p>
        <p className="text-[#555] text-xs mt-1">
          Which of these information layers will Corgee help manage for your organization? Select
          from the list below. If you see information layers with a similar function but you call it
          different, double-click to rename it or add a new information layer below.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 pb-3 gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-[#555]">Find by keyword:</span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search layers..."
            className="border border-[#ccc] rounded px-2 py-1 text-sm text-[#333] outline-none focus:border-[#62aacb] w-[200px]"
          />
        </div>

        <div className="flex items-center gap-3">
          {saveSuccess && (
            <span className="text-[12px] text-[#22b781] flex items-center gap-1">
              <span className="material-icons text-[14px]">check_circle</span>
              Saved successfully
            </span>
          )}
          {changes.size > 0 && (
            <>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 border border-[#ccc] text-[#fff] text-[12px] rounded bg-[#ccc]  disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:bg-[#5c6060] dark:hover:brightness-[0.85]"
              >
                <span className="material-icons text-[14px]" style={{ fontSize: '16px' }}>
                  close
                </span>
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 bg-[#62aacb] text-white text-[12px] rounded hover:bg-[#4e99ba] disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:bg-[#3c6c80] dark:hover:brightness-[0.85]"
              >
                {saving ? (
                  <>
                    <span
                      className="material-icons text-[14px] animate-spin"
                      style={{ fontSize: '16px' }}
                    >
                      refresh
                    </span>
                    Saving...
                  </>
                ) : (
                  <>
                    <span className="material-icons text-[14px]" style={{ fontSize: '16px' }}>
                      save
                    </span>
                    Save Changes
                  </>
                )}
              </button>
            </>
          )}
          <button
            onClick={handleAddLayer}
            disabled={newLayerIds.size > 0}
            className="flex items-center gap-1 text-[#62aacb] text-[12px] disabled:opacity-40 disabled:cursor-not-allowed group"
            title={newLayerIds.size > 0 ? 'Save or cancel the current new layer first' : undefined}
          >
            <span className="material-icons text-[16px] dark:text-[#beb7ac]">
              add_circle_outline
            </span>
            <span className="group-hover:underline">Add New Layer</span>
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-6 mb-3 px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828] flex items-center gap-2">
          <span className="material-icons text-[14px]">error_outline</span>
          {error}
        </div>
      )}

      {/* Column headers */}
      {!loading && filtered.length > 0 && (
        <div
          className="flex items-center gap-3 px-4 py-1 mx-6 bg-[#f0f0f0] border-b border-[#ddd] text-[11px] text-[#888] font-medium"
          style={{ borderLeft: '4px solid transparent' }}
        >
          <div className="w-4 flex-shrink-0" />
          <div className="flex-1 dark:text-[#b1a99d]">Name</div>
          <div className="flex-shrink-0 w-[100px] hidden sm:block text-right pr-1 dark:text-[#b1a99d]">
            Layer Color
          </div>
          <div className="flex-shrink-0 w-[100px] hidden sm:block text-right pr-1 dark:text-[#b1a99d]">
            Text Color
          </div>
          <div className="flex-shrink-0 w-[130px] hidden md:block text-right dark:text-[#b1a99d]">
            Preview
          </div>
        </div>
      )}

      {/* Content */}
      <div ref={listRef} className="flex-1 overflow-y-auto mx-6 mb-6">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <span className="material-icons animate-spin text-[#62aacb] text-[32px]">refresh</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-[#aaa] text-sm gap-2">
            <span className="material-icons text-[32px]">layers</span>
            {search ? 'No layers match your search.' : 'No layers found.'}
          </div>
        ) : (
          filtered.map(layer => (
            <LayerRow
              key={layer.LayerId}
              layer={layer}
              onChange={handleChange}
              isNew={newLayerIds.has(layer.LayerId)}
              autoEdit={layer.LayerId === justAddedId}
              securityGroups={layerSecGroupMap.get(layer.LayerId) ?? []}
              onEditSecurityGroups={() => openEditSg(layer.LayerId)}
            />
          ))
        )}
      </div>
      {/* ── Edit Security Groups modal ── */}
      {editingLayerId !== null &&
        createPortal(
          <div className="admin-console-portal fixed inset-0 z-[10003] flex items-center justify-center">
            <div className="fixed inset-0 bg-black/30" onClick={() => setEditingLayerId(null)} />
            <div className="relative z-10 bg-white rounded shadow-xl w-[460px] max-h-[80vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-4 pb-3">
                <div>
                  <h3 className="text-[16px] font-medium text-[#62aacb]">Edit Security Groups</h3>
                  <p className="text-[11px] text-[#999] mt-0.5">
                    Layer: <span className="text-[#333] font-medium">{editingLayer?.Name}</span>
                  </p>
                </div>
                <button
                  onClick={() => setEditingLayerId(null)}
                  className="text-[#bbb] hover:text-[#666]"
                >
                  <span className="material-icons text-[18px]">close</span>
                </button>
              </div>
              {/* Search */}
              <div className="flex items-center gap-3 px-5 pb-3">
                <span className="text-[12px] text-[#555] flex-shrink-0">Find by keyword:</span>
                <input
                  autoFocus
                  type="text"
                  value={sgSearch}
                  onChange={e => setSgSearch(e.target.value)}
                  className="flex-1 border border-[#ccc] rounded px-2 py-1 text-[12px] outline-none focus:border-[#62aacb]"
                />
              </div>
              {/* Tree */}
              <div className="flex-1 overflow-hidden mx-5 mb-3 border border-[#ddd] rounded flex flex-col">
                <div className="px-3 py-1.5 bg-[#f5f5f5] border-b border-[#ddd] text-[11px] font-semibold text-[#555]">
                  Name
                </div>
                <div className="flex-1 overflow-y-auto">
                  {filteredSgGroups.map(group =>
                  {
                    const isExpanded = sgSearch.trim() ? true : expandedSgGroups.has(group.GroupId);
                    const state = getSgGroupState(group);
                    return (
                      <div key={group.GroupId}>
                        <div className="flex items-center gap-2 px-3 py-2 bg-[#f9f9f9] border-b border-[#eee] hover:bg-[#f0f0f0]">
                          <span className="corgee_com_checkbox flex-shrink-0">
                            <input
                              id={`sg-group-${group.GroupId}`}
                              type="checkbox"
                              checked={state === 'all'}
                              ref={el =>
                              {
                                if (el) el.indeterminate = state === 'some';
                              }}
                              onChange={() => toggleSgGroupAll(group)}
                            />
                            <label htmlFor={`sg-group-${group.GroupId}`}></label>
                          </span>
                          <button
                            onClick={() => toggleSgGroupExpand(group.GroupId)}
                            className="flex items-center gap-1 flex-1 text-left"
                          >
                            <span className="material-icons text-[14px] text-[#888]">
                              {isExpanded ? 'expand_more' : 'chevron_right'}
                            </span>
                            <span className="text-[12px] font-semibold text-[#444]">
                              {group.Name}
                            </span>
                            <span className="text-[11px] text-[#aaa] ml-1">
                              ({group.SecurityGroups.length})
                            </span>
                          </button>
                        </div>
                        {isExpanded &&
                          group.SecurityGroups.map(sg => (
                            <div
                              key={sg.SecurityGroupID}
                              className="flex items-center gap-2 pl-7 pr-3 py-2 border-b border-[#f5f5f5] hover:bg-[#f9f9f9]"
                            >
                              <span className="corgee_com_checkbox flex-shrink-0">
                                <input
                                  id={`sg-sub-${sg.SecurityGroupID}`}
                                  type="checkbox"
                                  checked={selectedSgIds.has(sg.SecurityGroupID)}
                                  onChange={() => toggleSgSubGroup(sg.SecurityGroupID)}
                                />
                                <label htmlFor={`sg-sub-${sg.SecurityGroupID}`}></label>
                              </span>
                              <label
                                htmlFor={`sg-sub-${sg.SecurityGroupID}`}
                                className="text-[12px] text-[#333] cursor-pointer"
                              >
                                {sg.Name}
                              </label>
                            </div>
                          ))}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Footer */}
              <div className="px-5 py-3 border-t border-[#eee] flex justify-end gap-2">
                <button
                  onClick={() => setEditingLayerId(null)}
                  className="px-4 py-1.5 text-[12px] bg-[#999] text-white rounded hover:bg-[#777]"
                >
                  Cancel
                </button>
                <button
                  onClick={saveLayerSecurityGroups}
                  disabled={savingSg}
                  className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] disabled:opacity-50"
                >
                  {savingSg ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
